import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
  GetMessages,
  CreateMessage,
  CreateMessageReply,
  ClearMessagesStore,
} from './messages.actions';
import { CreateNewAlert } from '../global-alerts/global-alerts.actions';
import { throwError } from 'rxjs';
import { Message } from '@app/app/interfaces/message.model';
import { MessagesService } from '@app/app/services/messages.service';

export class MessagesStateModel {
  messages: Message[];
  loading: boolean;
}

@State<MessagesStateModel>({
  name: 'messages',
  defaults: {
    messages: [],
    loading: false,
  },
})
@Injectable()
export class MessagesState {
  @Selector()
  static messages(state: MessagesStateModel) {
    return state.messages;
  }

  @Selector()
  static loading(state: MessagesStateModel) {
    return state.loading;
  }

  constructor(
    private messagesService: MessagesService,
    private store: Store
  ) {}

  @Action(GetMessages)
  getMessages(
    { patchState }: StateContext<MessagesStateModel>,
    { dealId }: GetMessages
  ) {
    patchState({ loading: true });

    return this.messagesService.getMessages(dealId).pipe(
      catchError((error) => {
        patchState({ loading: false });

        this.store.dispatch(
          new CreateNewAlert({
            level: 'error',
            message: 'Unable to retrieve messages.',
          })
        );
        return throwError(() => error);
      }),
      tap((response) => {
        patchState({
          loading: false,
          messages: response.sort((a, b) =>
            new Date(a.created) < new Date(b.created) ? 1 : -1
          ),
        });
      })
    );
  }

  @Action(CreateMessage)
  createMessage(
    { getState, patchState }: StateContext<MessagesStateModel>,
    { dealId, description }: CreateMessage
  ) {
    return this.messagesService
      .createNewMessage(dealId, description)
      .pipe(
        catchError((error) => {
          this.store.dispatch(
            new CreateNewAlert({
              level: 'error',
              message: 'Unable to create message.',
            })
          );
          return throwError(() => error);
        }),
        tap((response) => {
          const messages = getState().messages;
          if (response) {
            patchState({ messages: [response, ...messages] });
          }
        })
      );
  }

  @Action(CreateMessageReply)
  createMessageReply(
    { getState, patchState }: StateContext<MessagesStateModel>,
    { dealId, taskId, description }: CreateMessageReply
  ) {
    return this.messagesService
      .createNewReply(dealId, taskId, description)
      .pipe(
        catchError((error) => {
          this.store.dispatch(
            new CreateNewAlert({
              level: 'error',
              message: 'Unable to create reply.',
            })
          );
          return throwError(() => error);
        }),
        tap((response) => {
          if (response) {
            const messages = [...getState().messages];
            const messageIndex = messages.findIndex(
              (message) => message.id === taskId
            );
            const message = { ...messages[messageIndex] };
            const replies = [...message.task_comments];
            replies.push(response);
            message.task_comments = replies;
            messages[messageIndex] = message;
            patchState({ messages });
          }
        })
      );
  }

  @Action(ClearMessagesStore)
  clearMessagesStore(
    { patchState }: StateContext<MessagesStateModel>,
    {}: ClearMessagesStore
  ) {
    patchState({ messages: [] });
  }
}
